/**
 * Detailed Documentation for All Node Types
 * 
 * This file contains the complete documentation content for each node type
 * to be displayed in the documentation hub
 */

export const nodeDocumentation = {
  'start': {
    overview: 'The Start Node is the entry point for every workflow. It marks where execution begins and receives initial input parameters.',
    whenToUse: [
      'Every workflow MUST have exactly ONE start node',
      'Beginning of manual workflow execution',
      'Receiving user input or API parameters',
      'Setting initial state variables'
    ],
    configuration: {
      required: ['id', 'type', 'position', 'data.label'],
      optional: ['data.initialVariables', 'data.expectedInputs']
    },
    example: `{
  id: 'start',
  type: 'start',
  position: { x: 100, y: 100 },
  data: {
    label: 'Start: Client Intake',
    initialVariables: {
      workflowName: 'Client Intake',
      startTime: '{{now}}'
    },
    expectedInputs: ['clientName', 'clientEmail', 'caseType']
  }
}`,
    bestPractices: [
      '✅ Use descriptive labels (e.g., "Start: Client Intake" not just "Start")',
      '✅ Document expected inputs in the configuration',
      '✅ Set default values using initialVariables',
      '✅ Always have exactly ONE start node per workflow',
      '❌ Never create multiple start nodes',
      '❌ Don\'t put complex logic in start - keep it simple'
    ],
    commonPatterns: [
      'Lead Intake: Start → Form → AI Agent → Conditional',
      'Triggered Workflow: Trigger → Start → Process Data',
      'Multi-Step Form: Start → Form 1 → Form 2 → Form 3'
    ]
  },
  
  'end': {
    overview: 'The End Node marks the completion of a workflow. It defines where execution terminates and can return final results.',
    whenToUse: [
      'End of successful workflow path',
      'After final result is produced',
      'Can have multiple end nodes for different exit paths',
      'Return data or status to caller'
    ],
    configuration: {
      required: ['id', 'type', 'position', 'data.label'],
      optional: ['data.finalMessage', 'data.successStatus', 'data.returnData']
    },
    example: `{
  id: 'end',
  type: 'end',
  position: { x: 100, y: 800 },
  data: {
    label: 'End: Success',
    finalMessage: 'Workflow completed successfully',
    successStatus: true,
    returnData: {
      result: '{{workflowResult}}',
      completedAt: '{{now}}'
    }
  }
}`,
    bestPractices: [
      '✅ Have at least ONE end node per workflow',
      '✅ Multiple end nodes OK for different exit paths',
      '✅ Use descriptive labels (e.g., "End: Approved", "End: Rejected")',
      '✅ Set finalMessage for user feedback',
      '❌ Don\'t forget to connect all paths to an end node'
    ],
    commonPatterns: [
      'Success Path: Process → End: Success',
      'Error Path: Error Handler → End: Error',
      'Conditional Exits: Conditional → End: Approved OR End: Rejected'
    ]
  },
  
  'ai-agent': {
    overview: 'The AI Agent Node calls Large Language Models (GPT-4, Claude, etc.) for intelligent text processing, analysis, and generation.',
    whenToUse: [
      'Text analysis, generation, or summarization',
      'Decision-making with reasoning',
      'Content creation and personalization',
      'Data extraction from unstructured text',
      'Classification and categorization',
      'Lead qualification and scoring'
    ],
    configuration: {
      required: ['model', 'systemPrompt', 'outputVariable'],
      optional: ['userPrompt', 'temperature', 'maxTokens', 'topP', 'frequencyPenalty']
    },
    example: `{
  type: 'ai-agent',
  data: {
    model: 'gpt-4',
    systemPrompt: 'You are a real estate lead qualification expert. Score leads as Hot/Warm/Cold based on budget, timeline, and pre-approval status. Provide reasoning.',
    userPrompt: 'Lead: {{leadName}}, Budget: {{budget}}, Timeline: {{timeline}}, Pre-approved: {{preApproved}}',
    temperature: 0.3,  // 0=focused, 1=creative
    maxTokens: 500,
    outputVariable: 'leadQualification'
  }
}`,
    bestPractices: [
      '✅ Use temperature 0-0.3 for factual tasks',
      '✅ Use temperature 0.7-1.0 for creative tasks',
      '✅ Clear, specific system prompts',
      '✅ Always store output in a variable',
      '✅ Test prompts thoroughly',
      '✅ Break complex tasks into multiple AI nodes',
      '❌ Don\'t make prompts too vague',
      '❌ Don\'t forget to set outputVariable'
    ],
    commonPatterns: [
      'Lead Qualification: Form → AI Agent (score) → Conditional',
      'Content Generation: Input → AI Agent (generate) → Email',
      'Text Analysis: Document → AI Agent (extract) → Database'
    ]
  },
  
  'form': {
    overview: 'The Form Node collects user input with structured fields, validation, and real-time workflow pause/resume.',
    whenToUse: [
      'Gather information from users',
      'Human-in-the-loop workflows',
      'Data entry points',
      'Multi-step forms',
      'User validation or approval'
    ],
    configuration: {
      required: ['formFields'],
      optional: ['title', 'description', 'submitButtonText', 'validation']
    },
    example: `{
  type: 'form',
  data: {
    label: 'Client Intake Form',
    formFields: [
      { name: 'fullName', type: 'text', label: 'Full Name', required: true },
      { name: 'email', type: 'email', label: 'Email', required: true },
      { name: 'budget', type: 'number', label: 'Budget', required: true, min: 0 },
      { name: 'propertyType', type: 'select', label: 'Property Type', 
        options: ['Single Family', 'Condo', 'Townhouse'], required: true },
      { name: 'comments', type: 'textarea', label: 'Comments', rows: 4 }
    ]
  }
}`,
    bestPractices: [
      '✅ Mark important fields as required',
      '✅ Use appropriate field types (email, tel, date, etc.)',
      '✅ Add helpful placeholder text',
      '✅ Include validation rules',
      '✅ Keep forms short when possible',
      '❌ Don\'t overwhelm users with too many fields',
      '❌ Don\'t forget to validate inputs'
    ],
    commonPatterns: [
      'Lead Capture: Start → Form → AI Qualify → Save',
      'Multi-Step: Form 1 → Form 2 → Form 3 → Submit',
      'Approval Flow: Process → Form (approve?) → Conditional'
    ]
  },
  
  'email': {
    overview: 'The Email Node sends emails with template support, personalization variables, and HTML formatting.',
    whenToUse: [
      'Send notifications and alerts',
      'Follow-up communications',
      'Reports and summaries',
      'Confirmations',
      'Marketing campaigns'
    ],
    configuration: {
      required: ['to', 'subject', 'body'],
      optional: ['cc', 'bcc', 'attachments', 'html', 'replyTo', 'from']
    },
    example: `{
  type: 'email',
  data: {
    to: '{{clientEmail}}',
    subject: 'Welcome, {{clientName}}!',
    body: \`Hi {{clientName}},

Thank you for your interest in {{propertyAddress}}.

I have 3 similar properties in your budget range. 
Can we schedule a call this week?

Best regards,
{{agentName}}\`,
    html: true,
    replyTo: 'agent@realty.com'
  }
}`,
    bestPractices: [
      '✅ Personalize with recipient\'s name',
      '✅ Clear, actionable subject lines',
      '✅ Mobile-friendly formatting',
      '✅ Include call-to-action',
      '✅ Test with various email clients',
      '✅ Use variables for dynamic content ({{name}})',
      '❌ Don\'t send without testing',
      '❌ Don\'t forget unsubscribe links (for marketing)'
    ],
    commonPatterns: [
      'Confirmation: Submit → Email (confirmation) → End',
      'Follow-up: Delay 24h → Email (follow-up) → Track',
      'Alert: Event → Email (notify) → Log'
    ]
  },
  
  'sms': {
    overview: 'The SMS Node sends text messages for time-sensitive notifications and reminders.',
    whenToUse: [
      'Urgent notifications',
      'Appointment reminders (24 hours before)',
      'Confirmations',
      'Status updates',
      'Two-factor authentication codes'
    ],
    configuration: {
      required: ['to', 'message'],
      optional: ['from', 'mediaUrl']
    },
    example: `{
  type: 'sms',
  data: {
    to: '{{clientPhone}}',  // E.164 format: +15555551234
    message: 'Reminder: Your showing at {{propertyAddress}} is tomorrow at {{time}}. See you there! - {{agentName}}'
  }
}`,
    bestPractices: [
      '✅ Keep messages under 160 characters',
      '✅ Include sender name',
      '✅ Clear call-to-action',
      '✅ Respect quiet hours (9 AM - 8 PM)',
      '✅ Provide opt-out option',
      '✅ Comply with TCPA regulations',
      '❌ Don\'t send promotional SMS without consent',
      '❌ Don\'t forget to include sender identification'
    ],
    commonPatterns: [
      'Reminder: Delay → SMS (24h before) → End',
      'Confirmation: Book → SMS (confirm) → Log',
      'Alert: Urgent Event → SMS (notify) → Follow-up'
    ]
  },
  
  'database': {
    overview: 'The Database Node performs CRUD operations on MongoDB collections for data persistence and retrieval.',
    whenToUse: [
      'Save form submissions',
      'Query existing records',
      'Update status or fields',
      'Delete old data',
      'Search and filter data'
    ],
    configuration: {
      required: ['operation', 'collection'],
      optional: ['query', 'recordData', 'updateData', 'outputVariable']
    },
    example: `{
  type: 'database',
  data: {
    operation: 'insert',  // or 'query', 'update', 'delete'
    collection: 'contacts',
    recordData: {
      name: '{{name}}',
      email: '{{email}}',
      phone: '{{phone}}',
      source: 'Website',
      leadScore: '{{aiScore}}',
      createdAt: '{{now}}'
    },
    outputVariable: 'savedContact'
  }
}`,
    bestPractices: [
      '✅ Always use outputVariable to capture results',
      '✅ Handle empty query results gracefully',
      '✅ Use database indexes for performance',
      '✅ Validate data before inserting',
      '✅ Use $set for partial updates',
      '❌ Don\'t query without indexes on large collections',
      '❌ Don\'t forget error handling'
    ],
    commonPatterns: [
      'Save: Form → Database (insert) → Email',
      'Search: Input → Database (query) → Display Results',
      'Update: Event → Database (update status) → Notify'
    ]
  },
  
  'conditional': {
    overview: 'The Conditional Node branches workflow execution based on conditions (if/else logic).',
    whenToUse: [
      'Different paths for different scenarios',
      'Validation checks',
      'Status-based routing',
      'Priority handling',
      'Lead scoring routes'
    ],
    configuration: {
      required: ['conditions'],
      optional: ['defaultOutput']
    },
    example: `{
  type: 'conditional',
  data: {
    conditions: [
      {
        field: 'leadScore',
        operator: 'equals',
        value: 'Hot',
        output: 'hot-lead-path'
      },
      {
        field: 'budget',
        operator: 'greaterThan',
        value: 1000000,
        output: 'luxury-path'
      }
    ],
    defaultOutput: 'standard-path'
  }
}`,
    bestPractices: [
      '✅ Always have a default/fallback path',
      '✅ Keep conditions simple and clear',
      '✅ Test all conditional paths',
      '✅ Use descriptive output names',
      '✅ Document edge cases',
      '❌ Don\'t create overly complex nested conditions',
      '❌ Don\'t forget the default path'
    ],
    commonPatterns: [
      'Lead Routing: Qualify → Conditional (Hot/Warm/Cold) → Different Actions',
      'Approval: Review → Conditional (Approved?) → Yes/No Paths',
      'Priority: Check → Conditional (Urgent?) → Fast/Normal'
    ]
  },
  
  'loop': {
    overview: 'The Loop Node repeats actions for multiple items in an array (forEach logic).',
    whenToUse: [
      'Process arrays or lists',
      'Batch operations',
      'Send multiple emails',
      'Update multiple records',
      'Process each item individually'
    ],
    configuration: {
      required: ['iterateOver'],
      optional: ['itemVariable', 'maxIterations', 'breakCondition', 'loopBodyNodes', 'aggregationStrategy']
    },
    example: `{
  type: 'loop',
  data: {
    iterateOver: 'matchedBuyers',  // Array variable
    itemVariable: 'buyer',         // Current item name
    maxIterations: 100,            // Safety limit
    breakCondition: 'buyer.contacted === true',
    loopBodyNodes: ['send-email', 'log-notification'],
    aggregationStrategy: 'accumulate'
  }
}`,
    bestPractices: [
      '✅ Set reasonable maxIterations',
      '✅ Use break conditions for early exit',
      '✅ Monitor performance with large arrays',
      '✅ Consider pagination for huge datasets',
      '✅ Log progress for debugging',
      '❌ Don\'t loop over unbounded arrays',
      '❌ Don\'t forget performance implications'
    ],
    commonPatterns: [
      'Notify Users: Get List → Loop (send email to each) → End',
      'Batch Update: Get Records → Loop (update each) → Save Results',
      'Data Processing: Load Items → Loop (process each) → Aggregate'
    ]
  },
  
  'trigger': {
    overview: 'The Trigger Node starts workflows automatically based on events like schedules, database changes, or webhooks.',
    whenToUse: [
      'Scheduled workflows (cron jobs)',
      'Database change detection',
      'Webhook endpoints',
      'Event-driven automation',
      'Automatic workflow initiation'
    ],
    configuration: {
      required: ['triggerType'],
      optional: ['schedule', 'webhookPath', 'collectionName', 'statusField', 'statusValue']
    },
    example: `{
  type: 'trigger',
  data: {
    triggerType: 'schedule',
    schedule: '0 9 * * 1'  // Every Monday at 9 AM (cron format)
  }
}

// Or database trigger:
{
  type: 'trigger',
  data: {
    triggerType: 'database_insert',
    collectionName: 'properties',
    statusField: 'status',
    statusValue: 'active'
  }
}`,
    bestPractices: [
      '✅ Test triggers thoroughly',
      '✅ Set appropriate debounce times',
      '✅ Log all trigger activations',
      '✅ Handle trigger failures gracefully',
      '✅ Document trigger conditions clearly',
      '❌ Don\'t create triggers that fire too frequently',
      '❌ Don\'t forget authentication on webhooks'
    ],
    commonPatterns: [
      'Weekly Report: Trigger (Mon 9AM) → Generate Report → Email',
      'New Listing: Trigger (DB insert) → Find Buyers → Notify',
      'Form Submit: Trigger (webhook) → Process → Save'
    ]
  },
  
  'api-call': {
    overview: 'The API Call Node makes HTTP requests to external services and APIs.',
    whenToUse: [
      'Call third-party APIs',
      'Send webhook notifications',
      'Fetch external data',
      'Integrate with external services',
      'Payment processing',
      'Address validation'
    ],
    configuration: {
      required: ['url', 'method'],
      optional: ['headers', 'body', 'timeout', 'retry', 'outputVariable']
    },
    example: `{
  type: 'api-call',
  data: {
    url: 'https://api.example.com/contacts',
    method: 'POST',
    headers: {
      'Authorization': 'Bearer {{apiKey}}',
      'Content-Type': 'application/json'
    },
    body: {
      name: '{{clientName}}',
      email: '{{clientEmail}}',
      source: 'Workflow Automation'
    },
    timeout: 30000,  // 30 seconds
    outputVariable: 'apiResponse'
  }
}`,
    bestPractices: [
      '✅ Store API keys in environment variables',
      '✅ Handle errors gracefully',
      '✅ Set appropriate timeouts',
      '✅ Log API responses',
      '✅ Use retry logic for critical calls',
      '❌ Don\'t hardcode API keys',
      '❌ Don\'t ignore response errors'
    ],
    commonPatterns: [
      'CRM Integration: Collect Data → API Call (CRM) → Confirm',
      'Payment: Calculate → API Call (Stripe) → Receipt',
      'Validation: Input → API Call (verify) → Proceed/Reject'
    ]
  },
  
  'delay': {
    overview: 'The Delay Node pauses workflow execution for a specified duration.',
    whenToUse: [
      'Schedule follow-ups (24 hours later)',
      'Rate limiting between API calls',
      'Wait for external process completion',
      'Time-based sequencing',
      'Drip campaigns'
    ],
    configuration: {
      required: ['delayAmount', 'delayUnit'],
      optional: ['skipWeekends', 'skipHolidays']
    },
    example: `{
  type: 'delay',
  data: {
    delayAmount: 24,
    delayUnit: 'hours',  // seconds, minutes, hours, days
    skipWeekends: true,
    skipHolidays: false
  }
}`,
    bestPractices: [
      '✅ Consider time zones',
      '✅ Account for weekends/holidays',
      '✅ Set reasonable delays',
      '✅ Provide way to cancel/skip delays',
      '✅ Log delay periods',
      '❌ Don\'t use for very short delays (< 1 second)',
      '❌ Don\'t forget delays block workflow execution'
    ],
    commonPatterns: [
      'Follow-up: Send Email → Delay 24h → Send Follow-up',
      'Reminder: Book Apt → Delay (until 24h before) → SMS Reminder',
      'Drip Campaign: Email 1 → Delay 3 days → Email 2 → Delay 7 days → Email 3'
    ]
  },
  
  'merge': {
    overview: 'The Merge Node combines data from multiple parallel branches into a single state.',
    whenToUse: [
      'After parallel processing',
      'Combine multiple data sources',
      'Aggregate results from parallel paths',
      'Synchronization point for parallel workflows'
    ],
    configuration: {
      required: [],
      optional: ['mergeStrategy', 'inputKeys', 'outputVariable']
    },
    example: `{
  type: 'merge',
  data: {
    mergeStrategy: 'combine_lists',  // or 'last_write_wins', 'deep_merge'
    inputKeys: ['apiResult1', 'apiResult2', 'dbResult'],
    outputVariable: 'mergedData'
  }
}`,
    bestPractices: [
      '✅ Use after Parallel nodes',
      '✅ Choose appropriate merge strategy',
      '✅ Document what gets merged',
      '✅ Handle missing data gracefully',
      '❌ Don\'t assume all branches complete',
      '❌ Don\'t forget to specify merge strategy'
    ],
    commonPatterns: [
      'Parallel APIs: Parallel (API1, API2, API3) → Merge → Process',
      'Multi-Source: DB + API + Cache → Merge → Display',
      'Fan-Out/Fan-In: Split → Process Each → Merge → Continue'
    ]
  },

  'web-crawler': {
    overview: 'The Web Crawler Node fetches and extracts content from web pages. It supports single or multiple URLs, optional link following (depth), and extraction modes: raw HTML, plain text, or CSS selectors.',
    whenToUse: [
      'Competitor or market page monitoring',
      'Content aggregation from public pages',
      'Lead enrichment from company websites',
      'Legal/Compliance page monitoring',
      'Ingesting blog or support articles for RAG'
    ],
    configuration: {
      required: ['urls or urlVariable'],
      optional: ['depth', 'maxPages', 'extractMode', 'selectors', 'respectRobotsTxt', 'requestDelayMs', 'timeoutMs', 'outputVariable']
    },
    example: `{
  type: 'web-crawler',
  data: {
    config: {
      urls: ['https://example.com/page1'],
      depth: 0,
      maxPages: 10,
      extractMode: 'text',
      respectRobotsTxt: true,
      outputVariable: 'crawled_content'
    }
  }
}`,
    bestPractices: [
      '✅ Respect robots.txt and rate limits',
      '✅ Use a descriptive User-Agent',
      '✅ Set reasonable timeout and maxPages',
      '✅ Use allowedDomains for production',
      '❌ Don\'t crawl PII-heavy pages without consent',
      '❌ Don\'t disable robots.txt for arbitrary sites'
    ],
    commonPatterns: [
      'Monitor: Trigger (schedule) → Web Crawler → Compare → Alert',
      'Enrich: Form (lead) → Web Crawler (company URL) → AI Agent → Save',
      'Ingest: Web Crawler → RAG (index) → Chat'
    ]
  },

  connector: {
    overview: 'The Connector Node runs operations (e.g. send message, create contact) against registered products like Slack, HubSpot, or a generic HTTP request. Credentials are stored separately and referenced by name.',
    whenToUse: [
      'Send Slack messages from a workflow',
      'Create or find HubSpot contacts',
      'Call any HTTP API with a stored token',
      'Integrate with CRM, ticketing, or other SaaS'
    ],
    configuration: {
      required: ['connectorId', 'operationId', 'credentialRef'],
      optional: ['inputMapping', 'outputVariable', 'stateVariables']
    },
    example: `{
  type: 'connector',
  data: {
    config: {
      connectorId: 'slack',
      operationId: 'send_message',
      credentialRef: 'connector_slack_token',
      inputMapping: {
        channel: '#general',
        text: '{{variables.message}}'
      },
      outputVariable: 'connector_result'
    }
  }
}`,
    bestPractices: [
      '✅ Store credentials in variables or env (CONNECTOR_<ref>)',
      '✅ Use inputMapping for dynamic values from state',
      '✅ Test in sandbox before production',
      '❌ Never store secrets in the flow JSON'
    ],
    commonPatterns: [
      'Notify: Process → Connector (Slack send_message) → Log',
      'CRM: Form (lead) → Connector (HubSpot create_contact) → Email',
      'API: Trigger → Connector (HTTP request) → Transform'
    ]
  },
  
  'code-executor': {
    overview: 'The Code Executor Node runs custom JavaScript or Python code for complex calculations and transformations.',
    whenToUse: [
      'Complex calculations (mortgage, ROI, etc.)',
      'Data transformation and formatting',
      'Custom validation logic',
      'Integration with custom libraries',
      'Advanced business logic'
    ],
    configuration: {
      required: ['language', 'code'],
      optional: ['timeout', 'outputVariable']
    },
    example: `{
  type: 'code-executor',
  data: {
    language: 'javascript',
    code: \`
      // Access state variables
      const budget = state.budget;
      const downPayment = state.downPayment;
      const interestRate = 0.065;  // 6.5%
      const years = 30;
      
      // Calculate monthly payment
      const principal = budget - downPayment;
      const monthlyRate = interestRate / 12;
      const payments = years * 12;
      const monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, payments)) / (Math.pow(1 + monthlyRate, payments) - 1);
      
      // Return result
      return {
        monthlyPayment: Math.round(monthlyPayment),
        totalPaid: Math.round(monthlyPayment * payments),
        totalInterest: Math.round((monthlyPayment * payments) - principal)
      };
    \`,
    outputVariable: 'mortgageCalculation'
  }
}`,
    bestPractices: [
      '✅ Keep code simple and focused',
      '✅ Handle errors with try/catch',
      '✅ Document complex logic',
      '✅ Test thoroughly',
      '✅ Set timeouts for long operations',
      '❌ Don\'t run untrusted code',
      '❌ Don\'t use for very long-running operations'
    ],
    commonPatterns: [
      'Calculation: Inputs → Code (calculate) → Display',
      'Transform: Raw Data → Code (format) → Save',
      'Validation: Data → Code (validate) → Conditional'
    ]
  },
  
  'rag': {
    overview: 'The RAG (Retrieval-Augmented Generation) Node queries your knowledge base and generates AI responses with retrieved context.',
    whenToUse: [
      'Answer questions from documents',
      'Search internal knowledge base',
      'Context-aware responses',
      'Company policy queries',
      'Product information lookup'
    ],
    configuration: {
      required: ['query', 'collectionName'],
      optional: ['topK', 'model', 'systemPrompt', 'outputVariable']
    },
    example: `{
  type: 'rag',
  data: {
    query: '{{userQuestion}}',
    collectionName: 'knowledge_base',
    topK: 5,  // Number of results to retrieve
    model: 'gpt-4',
    systemPrompt: 'Answer the question using only the provided context.',
    outputVariable: 'ragResponse'
  }
}`,
    bestPractices: [
      '✅ Index documents before using',
      '✅ Use specific queries for better results',
      '✅ Set appropriate topK (3-10 usually)',
      '✅ Review retrieved context quality',
      '❌ Don\'t use with empty knowledge base',
      '❌ Don\'t set topK too high (slow performance)'
    ],
    commonPatterns: [
      'Q&A: Question → RAG (search) → Answer',
      'Support: Issue → RAG (find solution) → Response',
      'Policy: Query → RAG (policy docs) → Explain'
    ]
  },

  'webhook': {
    overview: 'The Webhook Node creates HTTP endpoints to receive data from external systems.',
    whenToUse: [
      'Receive data from external systems',
      'Form builder integrations (Typeform, JotForm)',
      'Third-party notifications (Stripe, Zapier)',
      'Real-time integrations'
    ],
    configuration: {
      required: ['path', 'method'],
      optional: ['authentication', 'validateSignature', 'outputVariable']
    },
    example: `{
  type: 'webhook',
  data: {
    path: '/webhook/lead-capture',
    method: 'POST',
    authentication: 'bearer',
    validateSignature: true,
    outputVariable: 'webhookData'
  }
}`,
    bestPractices: [
      '✅ Validate webhook signatures',
      '✅ Return 200 OK quickly',
      '✅ Process asynchronously if heavy',
      '✅ Log all webhook calls',
      '✅ Handle duplicate calls (idempotency)',
      '❌ Don\'t expose webhooks without authentication',
      '❌ Don\'t do heavy processing synchronously'
    ],
    commonPatterns: [
      'External Form: Webhook (receive) → Process → Save → Notify',
      'Payment Event: Webhook (Stripe) → Update Order → Email',
      'Zapier Integration: Webhook → Transform → Database'
    ]
  },

  'parallel': {
    overview: 'The Parallel Node executes multiple workflow branches simultaneously for performance optimization.',
    whenToUse: [
      'Independent operations that can run concurrently',
      'Multiple API calls at once',
      'Parallel data processing',
      'Multi-channel notifications'
    ],
    configuration: {
      required: ['branches'],
      optional: ['waitForAll', 'outputVariable']
    },
    example: `{
  type: 'parallel',
  data: {
    branches: [
      { id: 'email', nodes: ['send-email'] },
      { id: 'sms', nodes: ['send-sms'] },
      { id: 'calendar', nodes: ['create-calendar-event'] }
    ],
    waitForAll: true,
    outputVariable: 'parallelResults'
  }
}`,
    bestPractices: [
      '✅ Use for independent operations only',
      '✅ Set timeouts for each branch',
      '✅ Handle branch failures gracefully',
      '✅ Use Merge node to combine results',
      '❌ Don\'t use for dependent operations',
      '❌ Don\'t create too many parallel branches (resource intensive)'
    ],
    commonPatterns: [
      'Multi-Channel: Parallel (Email + SMS + Push) → Merge → Log',
      'Data Enrichment: Parallel (API1 + API2 + API3) → Merge → Process',
      'Validation: Parallel (Check1 + Check2 + Check3) → Merge → Decide'
    ]
  }
};

export const edgeDocumentation = {
  'edge-basic': {
    overview: 'Basic Edge provides simple sequential connection between nodes.',
    whenToUse: [
      'Linear workflow progression',
      'Simple node-to-node connection',
      'Default connection type',
      'Sequential processing'
    ],
    example: '{ id: "e1", source: "node1", target: "node2", type: "edge-basic" }',
    visualStyle: 'Solid line, default color'
  },
  
  'edge-conditional': {
    overview: 'Conditional Edge routes execution based on conditions from Conditional nodes.',
    whenToUse: [
      'Multiple possible paths from Conditional node',
      'If/else routing',
      'Decision points',
      'Status-based routing'
    ],
    example: '{ id: "e1", source: "conditional", target: "hot-path", type: "edge-conditional", label: "Hot Lead" }',
    visualStyle: 'Labeled line with condition'
  },
  
  'edge-success': {
    overview: 'Success Edge executes only when the source node completes successfully.',
    whenToUse: [
      'Try-catch success path',
      'Happy path flow',
      'When operation succeeds',
      'Error-handled workflows'
    ],
    example: '{ id: "e1", source: "api-call", target: "process-result", type: "edge-success" }',
    visualStyle: 'Green line, often dashed'
  },
  
  'edge-error': {
    overview: 'Error Edge executes only when the source node fails or throws an error.',
    whenToUse: [
      'Error handling paths',
      'Failure recovery',
      'When operation fails',
      'Alert administrators'
    ],
    example: '{ id: "e1", source: "api-call", target: "error-handler", type: "edge-error" }',
    visualStyle: 'Red line, often dashed'
  }
};

// Additional Clio nodes
export const clioNodeDocumentation = {
  'clio-conflict-check': {
    overview: 'Checks for conflicts of interest in Clio practice management system.',
    whenToUse: ['New client intake', 'Before accepting a case', 'Ethical compliance checks'],
    configuration: { required: ['clientName'], optional: ['opposingParty', 'matterDescription'] },
    example: `{ type: 'clio-conflict-check', data: { clientName: '{{clientName}}', opposingParty: '{{opposingParty}}', outputVariable: 'conflictResult' }}`,
    bestPractices: ['✅ Run before creating matter', '✅ Check all parties involved', '❌ Don\'t skip for any client']
  },
  'clio-create-matter': {
    overview: 'Creates a new legal matter in Clio.',
    whenToUse: ['After conflict check passes', 'New case acceptance', 'Client onboarding'],
    configuration: { required: ['clientId', 'description'], optional: ['practiceArea', 'billingMethod', 'responsibleAttorney'] },
    example: `{ type: 'clio-create-matter', data: { clientId: '{{clioClientId}}', description: '{{caseDescription}}', practiceArea: 'Family Law', outputVariable: 'matterCreated' }}`,
    bestPractices: ['✅ Verify client exists first', '✅ Set billing method', '❌ Don\'t create without conflict check']
  },
  'clio-post-time': {
    overview: 'Logs billable hours to a Clio matter.',
    whenToUse: ['After client meetings', 'Automated time entry', 'Billable hours tracking'],
    configuration: { required: ['matterId', 'hours'], optional: ['date', 'description'] },
    example: `{ type: 'clio-post-time', data: { matterId: '{{matterId}}', hours: 1.5, description: 'Client consultation', outputVariable: 'timeEntry' }}`,
    bestPractices: ['✅ Round to billing increments', '✅ Add detailed descriptions', '❌ Don\'t forget to log all billable time']
  },
  'clio-upload-document': {
    overview: 'Uploads documents to a Clio matter.',
    whenToUse: ['Store contracts and agreements', 'Client document management', 'Case file organization'],
    configuration: { required: ['matterId', 'documentUrl'], optional: ['documentName', 'category'] },
    example: `{ type: 'clio-upload-document', data: { matterId: '{{matterId}}', documentUrl: '{{docUrl}}', category: 'Contract', outputVariable: 'uploadResult' }}`,
    bestPractices: ['✅ Categorize documents', '✅ Use descriptive names', '❌ Don\'t upload without virus scan']
  }
};

